import { type FC, useMemo } from 'react';

import { RenderingProvider, useCms, useContent } from '@eneco/cms';
import { Shadow } from '@eneco/custom-components/Registration';
import useDC from '@eneco/dc/useDC';
import { Box, EnecoLogo, NavLink, OxxioLogo, PageGrid, Stack, WoonenergieLogo } from '@eneco/sparky';
import { DC_Repositories_Base_Enumerations_Label } from '@monorepo-types/dc';

import type { RegistrationHeaderContent } from './registration-header-content.types';

const getLogo = (label: DC_Repositories_Base_Enumerations_Label) => {
  switch (label) {
    case 'oxxio':
      return OxxioLogo;
    case 'woonenergie':
      return WoonenergieLogo;
    case 'eneco':
    default:
      return EnecoLogo;
  }
};

const RegistrationHeaderInner = () => {
  const { label } = useDC();
  const content = useContent<RegistrationHeaderContent>();
  const Logo = getLogo(label);
  return (
    <Shadow>
      <Box backgroundColor="backgroundPrimary" paddingY="3">
        <PageGrid>
          <PageGrid.Item gridColumn="1/-1">
            <Stack direction="row" alignX="justify" alignY="center">
              <Logo />
              <Stack direction="row" gap="4">
                <NavLink href={content.customerServiceLink?.href} variant="tertiary">
                  {content.customerServiceLink?.text}
                </NavLink>
              </Stack>
            </Stack>
          </PageGrid.Item>
        </PageGrid>
      </Box>
    </Shadow>
  );
};

interface RegistrationHeaderProps {
  fields?: unknown;
  params?: Record<string, string>;
  placeholders?: Record<string, unknown[]>;
  componentName?: string;
  uid?: string;
}

const RegistrationHeader: FC<RegistrationHeaderProps> = ({ fields, params, placeholders, componentName, uid }) => {
  const { adapter } = useCms();
  const rendering = useMemo(
    () =>
      adapter.toRendering({
        componentName: componentName ?? 'RegistrationHeader',
        uid,
        fields,
        params,
        placeholders,
      }),
    [adapter, fields, params, placeholders, componentName, uid],
  );

  return (
    <RenderingProvider rendering={rendering}>
      <RegistrationHeaderInner />
    </RenderingProvider>
  );
};

export default RegistrationHeader;
